import { useEffect, useState } from 'react'
import { router, useForm } from '@inertiajs/react'
import { Info, Link2, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { CredentialFieldset } from '@/frontend/components/Credentials/CredentialFieldset'
import { normalizeIdentifier } from '@/frontend/components/Credentials/CredentialFormModal'
import { cn } from '@/lib/utils'
import type { SharedProps } from '@/frontend/types/inertia'

interface AttachableCredential {
  id: string
  name: string
}

interface Props {
  open: boolean
  onClose: () => void
  workerId: string
  workerName: string
  attachableCredentials: AttachableCredential[]
}

type Tab = 'reuse' | 'new'

interface FieldRow {
  key: string
  value: string
  _id: string
  // Inertia's useForm requires FormDataConvertible values; the string index
  // signature (mirroring CredentialField) satisfies that constraint.
  [key: string]: string
}

let fieldIdCounter = 0
const nextFieldId = () => `connect-field-${++fieldIdCounter}`
const newField = (): FieldRow => ({ key: '', value: '', _id: nextFieldId() })

/**
 * Conexões do agente — conectar credencial (Iteração 3). O portal do agente
 * ganha UM lugar de conectar de verdade: reaproveitar uma credencial que já
 * existe na organização (aba "Reaproveitar") ou criar uma nova que nasce como
 * recurso da organização já anexado a este agente (aba "Criar nova"). Não é a
 * lista de admin filtrada — é a ação de conectar, sem beco sem saída.
 */
export default function ConnectCredentialModal({
  open,
  onClose,
  workerId,
  workerName,
  attachableCredentials,
}: Props) {
  const [tab, setTab] = useState<Tab>('reuse')
  // Attach da aba Reaproveitar é otimista o suficiente: marca a linha como
  // anexada no clique; o redirect do Inertia recarrega a lista sem ela.
  const [attachingId, setAttachingId] = useState<string | null>(null)
  const [attachedIds, setAttachedIds] = useState<Set<string>>(new Set())

  const form = useForm<{ digital_worker_id: string; name: string; fields: FieldRow[] }>({
    digital_worker_id: workerId,
    name: '',
    fields: [newField()],
  })

  // Sem credenciais reaproveitáveis, "Criar nova" é o único caminho — abre nela.
  useEffect(() => {
    if (open) {
      setTab(attachableCredentials.length === 0 ? 'new' : 'reuse')
      setAttachedIds(new Set())
      setAttachingId(null)
      form.setData({ digital_worker_id: workerId, name: '', fields: [newField()] })
      form.clearErrors()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workerId])

  const attach = (credentialId: string) => {
    setAttachingId(credentialId)
    router.post(
      `/dashboard/digital_workers/${workerId}/toggle_credential`,
      { credential_id: credentialId, enabled: true },
      {
        preserveScroll: true,
        onSuccess: () => {
          setAttachedIds((prev) => new Set(prev).add(credentialId))
          onClose()
        },
        onFinish: () => setAttachingId(null),
      },
    )
  }

  const addField = () => form.setData('fields', [...form.data.fields, newField()])
  const removeField = (index: number) => {
    if (form.data.fields.length <= 1) return
    form.setData('fields', form.data.fields.filter((_, i) => i !== index))
  }
  const updateField = (index: number, key: 'key' | 'value', value: string) => {
    const next = key === 'key' ? normalizeIdentifier(value) : value
    form.setData('fields', form.data.fields.map((f, i) => (i === index ? { ...f, [key]: next } : f)))
  }

  const hasInvalidFields = form.data.fields.some((f) => !f.key.trim() || !f.value.trim())
  const canCreate = !form.processing && form.data.name.trim() !== '' && !hasInvalidFields

  const create = () => {
    form.transform((data) => ({
      digital_worker_id: data.digital_worker_id,
      name: data.name,
      fields: data.fields.map(({ _id, ...rest }) => rest),
    }))
    form.post('/dashboard/credentials', {
      preserveScroll: true,
      onSuccess: (page) => {
        const flash = (page.props as unknown as SharedProps).flash
        if (flash?.alert) return
        form.reset()
        onClose()
      },
    })
  }

  const handleClose = () => {
    form.reset()
    form.clearErrors()
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Conectar credencial à {workerName}</DialogTitle>
          <DialogDescription>Reaproveite uma da organização ou crie uma nova.</DialogDescription>
        </DialogHeader>

        {/* Segmentado (padrão do protótipo): fill rebaixado com a aba ativa
            "levantada" em bg-card. */}
        <div className="flex gap-0.5 rounded-[9px] bg-secondary p-[3px]" role="tablist" aria-label="Como conectar">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'reuse'}
            onClick={() => setTab('reuse')}
            className={cn(
              'flex-1 rounded-[7px] px-3 py-1.5 text-[12.5px] transition-colors duration-150',
              tab === 'reuse'
                ? 'bg-card font-semibold text-foreground shadow-xs'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            Reaproveitar da organização
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'new'}
            onClick={() => setTab('new')}
            className={cn(
              'flex-1 rounded-[7px] px-3 py-1.5 text-[12.5px] transition-colors duration-150',
              tab === 'new'
                ? 'bg-card font-semibold text-foreground shadow-xs'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            Criar nova
          </button>
        </div>

        {tab === 'reuse' ? (
          <div role="tabpanel" className="max-h-[340px] overflow-y-auto">
            {attachableCredentials.length === 0 ? (
              <p className="py-6 text-center text-[13px] text-muted-foreground">
                Nenhuma credencial da organização para reaproveitar. Crie uma nova.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {attachableCredentials.map((credential) => {
                  const done = attachedIds.has(credential.id)
                  return (
                    <li key={credential.id} className="flex items-center gap-3 py-2.5">
                      <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground" title={credential.name}>
                        {credential.name}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={done || attachingId === credential.id}
                        onClick={() => attach(credential.id)}
                        aria-label={`Anexar ${credential.name} à ${workerName}`}
                      >
                        {attachingId === credential.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Link2 className="h-3.5 w-3.5" />
                        )}
                        {done ? 'Anexada' : 'Anexar'}
                      </Button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        ) : (
          <div role="tabpanel" className="space-y-3">
            <CredentialFieldset
              idPrefix="connect-cred"
              name={form.data.name}
              onNameChange={(value) => form.setData('name', normalizeIdentifier(value))}
              nameError={form.errors.name}
              fields={form.data.fields}
              onFieldChange={updateField}
              onAddField={addField}
              onRemoveField={removeField}
              fieldsError={form.errors.fields}
            />

            <div className="flex gap-2 rounded-[9px] bg-brand/10 px-3 py-2.5 dark:bg-brand/15">
              <Info className="mt-0.5 h-3.5 w-3.5 flex-none text-brand" strokeWidth={1.75} />
              <p className="text-[11.5px] text-muted-foreground">
                Fica salva na organização e já conectada à {workerName} — outros colaboradores podem reaproveitar depois.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {tab === 'reuse' ? 'Concluir' : 'Cancelar'}
          </Button>
          {tab === 'new' && (
            <Button onClick={create} disabled={!canCreate}>
              {form.processing && <Loader2 className="h-4 w-4 animate-spin" />}
              {form.processing ? 'Criando…' : 'Criar e conectar'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
